import React, { useCallback, useEffect } from 'react'
import { Route, Routes } from 'react-router-dom'
import { datadogRum } from '@datadog/browser-rum'

import { useAppSettings, useUser } from './contexts'
import Layout from './components/Layout/Layout'
import { routes } from './routes'
import ProtectedRoute from './components/ProtectedRoute'
import { useQueryParamNotification } from './hooks/useQueryParamNotification'
import { useReferrerCookie } from './hooks/useReferrerCookie'
import { useModalReminders, useVerifyToken } from './hooks'
import { useFeatureFlags } from './hooks/useFeatureFlag'
import RouteElement from './components/RouteElement/RouteElement'
import { getEnv } from './config'

// Avoid type errors with Pendo which is installed globally in index.html
const pendo = window.pendo

const checkAccess = ({ user, accessRoles, hasRoles }) => {
  if (accessRoles) {
    if (!user?.isConfirmed) {
      return false
    }
    if (user.isAdmin) {
      return true
    }
    return accessRoles.length ? hasRoles(accessRoles) : true
  }
  return true
}

function App() {
  const { user, hasRoles } = useUser()
  const { appSettings } = useAppSettings()
  const env = getEnv()

  useQueryParamNotification()
  useVerifyToken()
  useModalReminders()

  const { setReferrerCookie } = useReferrerCookie()
  const { setFeatureFlagsFromSearchParams } = useFeatureFlags()

  // Set the referrer cookie once on load
  useEffect(() => {
    setReferrerCookie()
    setFeatureFlagsFromSearchParams()
  }, [])

  const makeRoutes = useCallback(
    (routesToMake, depth = 0) => {
      return routesToMake.map(
        (
          {
            children,
            element,
            accessRoles,
            title,
            isRoot = false,
            ...routeProps
          },
          index,
        ) => {
          const hasAccess = checkAccess({ user, accessRoles, hasRoles })
          const subRoutes = children && makeRoutes(children, depth + 1)

          const Element = hasAccess
            ? element
            : () => <ProtectedRoute user={user} />

          return (
            <Route
              key={`app-route-${index}-${depth}`}
              element={
                <RouteElement title={title} Element={Element} isRoot={isRoot} />
              }
              {...routeProps}
            >
              {subRoutes}
            </Route>
          )
        },
      )
    },
    [user, hasRoles],
  )

  if (user && user.id) {
    datadogRum.setUser({
      id: user.id,
      name: user.fullName,
      email: user.email,
      isAdmin: user.isAdmin,
      isAgent: user.isAgent,
    })
  }

  if (
    user &&
    appSettings &&
    ['staging', 'test', 'production', 'local'].includes(env)
  ) {
    const pendoOpts = {
      visitor: {
        id: `${env}-${user.id}`,
        email: user.email || '',
        full_name: user.fullName,
        // role:         // Optional

        // You can add any additional visitor level key-values here,
        // as long as it's not one of the above reserved names.
      },

      account: {
        id: `${env}-${appSettings.id}`,
        name: appSettings.name,
        // is_paying:    // Recommended if using Pendo Feedback
        // monthly_value:// Recommended if using Pendo Feedback

        isPremium: true, // TODO - add this to appSettings
        // planLevel:    // Optional
        // planPrice:    // Optional
        creationDate: appSettings.createdAt,

        // You can add any additional account level key-values here,
        // as long as it's not one of the above reserved names.
      },

      cookieDomain: '.harvestiq.com',
    }

    pendo.initialize(pendoOpts)
  }

  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        {makeRoutes(routes)}
      </Route>
    </Routes>
  )
}

export default App
